import { ofetch } from 'ofetch'
import { useAuthStore } from '@/stores/auth'
import { setActionNotice } from '@/lib/action-notice'

/**
 * Shared HTTP client. Auth interceptor attaches the Cognito ID token; 401
 * responses trigger a re-login (handled by the auth store). Services in
 * `src/api/*` import this and never call `ofetch` directly.
 */
export const apiClient = ofetch.create({
  baseURL: import.meta.env.VITE_API_URL,
  retry: 0,
  // Default request timeout. Without this a hung backend leaves the UI in a
  // loading state indefinitely. API Gateway (HTTP API) cuts any integration
  // off at 29s, so nothing can legitimately take longer — including the
  // agentic notes-generate, which budgets its loop to answer (note or
  // fallback) under that cap. 35s rather than 29-30: this clock also counts
  // DNS/TLS/connect, and when the server does hit its cap the useful error is
  // the server's own — a near-equal client timeout would race it and surface
  // a generic client abort instead.
  timeout: 35_000,

  // Both interceptors below call `useAuthStore()` synchronously. Pinia is
  // installed in main.ts BEFORE any router navigation or component mount, so
  // by the time any apiClient call fires the store is always ready. If you
  // ever introduce a code path that calls apiClient during plugin install
  // (i.e. before main.ts finishes), expect "no active pinia" at this line.
  async onRequest({ options }) {
    const authStore = useAuthStore()
    const token = await authStore.getIdToken()
    if (token) {
      const headers = new Headers(options.headers as HeadersInit | undefined)
      headers.set('Authorization', `Bearer ${token}`)
      options.headers = headers
    }
  },

  onResponse({ response }) {
    // Non-blocking warning header: a write proceeded but we couldn't verify the
    // record was still for sale on Discogs (Discogs unreachable). Surface a toast
    // so staff double-check it didn't already sell there. (Requires the API to
    // expose this header via CORS — see exposeHeaders in api-stack.ts.)
    if (response.headers.get('x-discogs-unverified')) {
      setActionNotice('WARN_DISCOGS_UNVERIFIED')
    }
  },

  onResponseError({ response }) {
    if (response.status === 401) {
      // Silent refresh failed → kick off interactive login.
      useAuthStore().handleAuthFailure()
    }
  },
})
